import {
  opportunitiesStatusesCreateMutation,
  opportunitiesStatusesDestroyMutation,
  opportunitiesStatusesListOptions,
  opportunitiesStatusesListQueryKey,
  opportunitiesStatusesPartialUpdateMutation,
  opportunitiesStatusesRetrieveOptions,
  PaginatedReturnOpportunityStatusListReadable,
  PatchedReturnOpportunityStatusWritable,
  ReturnOpportunityStatusReadable
} from '@crm/api'
import { ConfirmModal, DndTable } from '@crm/components'
import {
  FormModal,
  FormNomenclaturesStatus
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Form } from 'antd'
import { Add, Edit, Trash } from 'iconsax-react'

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'opportunity-status'

export const NomenclaturesOpportunityStatus = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const { resetPagination } = useResetPagination()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()

  const [form] = Form.useForm()

  const [data, setData] =
    useState<PaginatedReturnOpportunityStatusListReadable>()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [sorted, setSorted] = useState(false)

  const { data: statusList, isLoading } = useQuery(
    opportunitiesStatusesListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  useEffect(() => {
    if (statusList) {
      setData(statusList)
    }
  }, [statusList])

  const { mutate: updateMutate } = useMutation({
    ...opportunitiesStatusesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => invalidateQueries([opportunitiesStatusesListQueryKey()])
  })

  const { mutate: deleteMutate } = useMutation({
    ...opportunitiesStatusesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([opportunitiesStatusesListQueryKey()])
      setDeleteItem(undefined)
      resetPagination()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  useEffect(() => {
    if (data?.results && sorted) {
      setData(
        (prevState) =>
          ({
            ...prevState,
            results: data?.results?.map(({ id, pipeline, ...params }, i) => {
              if (
                i + 1 !==
                statusList?.results?.find((item) => item.id === id)?.order
              ) {
                updateMutate({
                  path: { id },
                  body: {
                    ...params,
                    pipeline: pipeline?.id,
                    order: i + 1
                  } as unknown as PatchedReturnOpportunityStatusWritable
                })

                return { id, ...params, order: i + 1 }
              }

              return { id, ...params }
            })
          } as PaginatedReturnOpportunityStatusListReadable)
      )
      setSorted(false)
    }
  }, [data, sorted, statusList?.results, updateMutate])

  const onDelete = () => {
    if (deleteItem) {
      deleteMutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        icon: <Trash size={20} />,
        danger: true,
        key: 'delete',
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnOpportunityStatusReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.code'),
        dataIndex: 'code',
        key: 'code',
        sorter: true
      },
      {
        title: t('nomenclature.order'),
        dataIndex: 'order',
        key: 'order',
        sorter: true
      },
      {
        title: t('nomenclature.is_default'),
        key: 'is_default',
        sorter: true,
        render: ({ is_default }) =>
          is_default ? t('buttons.yes') : t('buttons.no')
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const onSortEnd = (
    action: (
      state: ReturnOpportunityStatusReadable[]
    ) => ReturnOpportunityStatusReadable[]
  ) => {
    setData((prev) => {
      const results = action(prev?.results || [])

      return {
        ...prev,
        results: results || prev?.results
      } as PaginatedReturnOpportunityStatusListReadable
    })

    setSorted(true)
  }

  const dndItems = data?.results?.map(({ id }) => id) || []

  return (
    <>
      <DndTable
        onDragEnd={onSortEnd}
        items={dndItems}
        data={data}
        rowKey="id"
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        rightChildren={
          <Button
            type="primary"
            icon={<Add size={20} />}
            onClick={() => setIsCreate(true)}
          >
            {t('nomenclature.add_status')}
          </Button>
        }
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={opportunitiesStatusesRetrieveOptions}
        onCreate={opportunitiesStatusesCreateMutation}
        onUpdate={opportunitiesStatusesPartialUpdateMutation}
        onCloseModal={onCloseModal}
        form={form}
      >
        <FormNomenclaturesStatus form={form} />
      </FormModal>
    </>
  )
}
